import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { formatOptionsList } from '../../../../../../features/formatters'

import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'

import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { DV_360_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { PIXELS } from '../../../LineItemDv360Create/LineItemDv360CreateForm/LineItemDV360CreateMainForm/fields'
import { getInitialValues } from './fields'

function PixelsSectionForm({ editItemData: item, title, ...formProps }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const selectedDV360AdAccount = useSelector(selectedDV360AdAccountSelector)
  const { id: selectedAdAccountId, dv360_floodlight_groups: dv360FloodlightGroups } = selectedDV360AdAccount

  const formattedPixels = useMemo(
    () =>
      formatOptionsList({
        list: dv360FloodlightGroups,
        labelName: 'name',
        valueName: 'id'
      }),
    [dv360FloodlightGroups]
  )

  const initialValues = useMemo(() => {
    return getInitialValues(item, formattedPixels)
  }, [item, formattedPixels])

  const handleSubmit = useCallback(
    values => {
      const updateData = {
        account: selectedAdAccountId,
        conversion_counting: {
          floodlight_activity_configs: values[PIXELS].map(pixel => ({
            floodlight_activity_id: pixel.value
          }))
        },
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateData, item.id, DV_360_PLATFORM))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values, errors, touched } = formik

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || lineItemWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <>
        <MultiSelectBox
          placeholder={t('Select pixels')}
          options={formattedPixels}
          name={PIXELS}
          value={values[PIXELS]}
          isLoading={false}
          setFieldValue={setFieldValue}
          isSearchable={false}
        />
        {errors[PIXELS] && touched[PIXELS] && <ErrorMessage error={errors[PIXELS]} />}
      </>
    </EditForm>
  )
}

export default PixelsSectionForm
